import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@mesterplan/auth-react';
import { useMesterplanApi } from '@mesterplan/web-hooks';
import { CreateFirstCompanyScreen } from '@/components/onboarding/CreateFirstCompanyScreen';
import { PendingMembershipScreen } from '@/components/onboarding/PendingMembershipScreen';
import type { PendingCompanyOption } from '@/components/onboarding/PendingMembershipScreen';

interface OnboardingGateProps {
  /** Called with the new org id after a successful bootstrap. */
  onCreated: (orgId: string) => void;
  /** A pending membership became a real one: reload the organisations. */
  onActivated: () => void;
}

/**
 * What a user with no company sees (#3702): if they have been offered a company whose
 * invitation waits on their email confirmation, say so BEFORE "Opret din virksomhed" (so a
 * legitimate invitee does not create a stray company). Otherwise the ordinary onboarding.
 * A failed lookup falls back to the ordinary onboarding; it is never a dead end.
 */
export function OnboardingGate({ onCreated, onActivated }: OnboardingGateProps) {
  const api = useMesterplanApi();
  const { user } = useAuth();
  const [pending, setPending] = useState<ReadonlyArray<PendingCompanyOption> | null>(null);
  const [createOwn, setCreateOwn] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await api.me.pendingMemberships();
        if (!cancelled) setPending(res.ok ? res.data : []);
      } catch {
        if (!cancelled) setPending([]);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [api]);

  if (pending === null) {
    return (
      <div className="flex min-h-screen items-center justify-center" role="status" aria-label="Indlæser">
        <Loader2 className="h-6 w-6 animate-spin text-muted" />
      </div>
    );
  }

  if (pending.length > 0 && !createOwn) {
    return (
      <PendingMembershipScreen
        companies={pending}
        email={user?.email ?? null}
        onActivated={onActivated}
        onCreateOwn={() => setCreateOwn(true)}
      />
    );
  }

  return <CreateFirstCompanyScreen onCreated={onCreated} />;
}
